import { PaginatorData } from 'polly-qubit';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

export const genericExponentialRetryStrategy =
  ({
    maxRetryAttempts = 5,
    scalingDuration = 1000,
  }: {
    maxRetryAttempts?: number;
    scalingDuration?: number;
    excludedStatusCodes?: number[];
  } = {}) =>
  (attempts: Observable<any>): Observable<number> => {
    return attempts.pipe(
      mergeMap((error, i) => {
        const retryAttempt = i + 1;
        /** If maximum number of retries have been met,
         * or response is a status code we don't wish to retry, throw error
         */
        if (retryAttempt > maxRetryAttempts) {
          return throwError(error);
        }
        return timer(Math.pow(2, retryAttempt) * scalingDuration);
      })
    );
  };

    export function smoothScroll(element: HTMLElement, targetScroll: number, duration: number): void {
      const startScroll = element.scrollLeft;
      const change = targetScroll - startScroll;
      let currentTime = 0;
      const animateScroll = (): void => {
        currentTime += 20;
        const val = easeInOutQuad(currentTime, startScroll, change, duration);
        element.scrollLeft = val;
        if (currentTime < duration) {
          requestAnimationFrame(animateScroll);
        }
      };
      animateScroll();
    }

    function easeInOutQuad(currentTime: number, startScroll: number, change: number, duration: number): number {
      currentTime /= duration / 2;
      if (currentTime < 1) return change / 2 * currentTime * currentTime + startScroll;
      currentTime--;
      return -change / 2 * (currentTime * (currentTime - 2) - 1) + startScroll;
    };
/**
 * Creates a dummy target element for positioning an table sub-overlay panel.
 * Positions the dummy element based on the available space relative to the target element.
 *
 * @param {HTMLElement} target - The element to append the dummy target to and use for positioning.
 * @returns {HTMLElement} - The dummy target element.
 */
export function getOverlayTarget(target: HTMLElement): HTMLElement {
  const targetWidth = target.clientWidth;
  const targetRect = target.getBoundingClientRect();
  let modifiedEl = document.getElementById('custom-positioning-div') || document.createElement('div');
  modifiedEl.id = 'custom-positioning-div';
  modifiedEl.style.height = '0';
  modifiedEl.style.position = 'absolute';
  const viewportWidth = window.innerWidth;
  const spaceOnRight = viewportWidth - (targetRect.right + 240);
  if (spaceOnRight > 0) {
    modifiedEl.style.width = `${targetWidth}px`;
    modifiedEl.style.left = `${targetWidth}px`;
    modifiedEl.style.right = 'auto';
  } else {
    modifiedEl.style.width = `240px`;
    modifiedEl.style.left = 'auto';
    modifiedEl.style.right = `${targetWidth}px`;
  }
  target.append(modifiedEl);
  return modifiedEl;
}

export function extractErrorDetails(
  error: HttpErrorResponse | Error,
  defaultTitle: string,
  defaultDetail: string
): { title: string; detail: string } {
  if (!error) {
    return { title: defaultTitle, detail: defaultDetail };
  }

  if (error instanceof HttpErrorResponse) {
    const firstError = error.error?.errors?.[0];
    if (firstError) {
      const title = firstError.title || defaultTitle;
      const detail = firstError.detail || defaultDetail;
      return { title, detail };
    }
  } else {
    const detail = error.message || defaultDetail;
    return { title: defaultTitle, detail };
  }
  return { title: defaultTitle, detail: defaultDetail };
}

/**
 * Filters items from arrayA by matching values in arrayB using the specified keys.
 *
 * @param arrayA - The array to filter.
 * @param arrayB - The array to match values from.
 * @param keyA - Key in arrayA to compare.
 * @param keyB - Key in arrayB to compare.
 * @returns Filtered array of items from arrayA that have matching values in arrayB.
 */
export function filterByMatchingKey<
  T extends Record<string, any>,
  U extends Record<string, any>,
  K1 extends keyof T,
  K2 extends keyof U,
  V = T[K1] & U[K2] // Ensure T[K1] and U[K2] are compatible types
>(arrayA: T[], arrayB: U[], keyA: K1, keyB: K2): T[] {
  const lookup = new Set<V>(arrayB.map((item) => item[keyB] as V));
  return arrayA.filter((item) => lookup.has(item[keyA] as V));
}

/**
 * This function formats the header title by inserting the valueToInsert after the first word
 * @param title - The title of the application
 * @param valueToInsert - The dataset ID to be inserted into the title
 * @returns The formatted title with valueToInsert inserted
 */
export function insertAfterFirstWord(title: string, valueToInsert: string): string {
  const splitTitle = title.split(' ');
  if (valueToInsert) {
    splitTitle.splice(1, 0, valueToInsert);
  }
  const joinTitle = splitTitle.join(' ');
  return joinTitle;
}

export function paginatorInitialData(rows: number): PaginatorData {
  return {
    first: 0,
    page: 1,
    pageCount: 0,
    rows,
  };
}

export function isNonEmptyArray<T>(arr: readonly T[] | null | undefined): boolean {
  return Array.isArray(arr) && arr.length > 0;
}
